import { computed, defineComponent, h, ref } from 'vue'
import { QBtn, QIcon } from 'quasar'

import { bsComponentProps, BsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { quasarHandlers } from '../quasarHandlers'

/**
 * Every button in the apps. Wraps QBtn and adds the house variants plus one behaviour worth naming: a
 * click is only emitted ONCE per 500ms, because a double-tapped submit used to send two of everything.
 *
 * `isDisabled` is unused by the render — see the note on it.
 *
 * Its props and helpers are exported because BsBtnDropdown and BsFilterBtn render their own element over
 * the same contract; they used to inherit them by extending this class.
 */
export const bsBtnProps = {
  ...bsComponentProps,
  secondary: { type: Boolean, default: false },
  warning: { type: Boolean, default: false },
  /**
   * Deliberately WITHOUT a default, unlike every other Boolean here: Vue's own casting then makes an
   * absent `active` false (so `renderActiveIcon` renders nothing) while an explicitly passed `undefined`
   * stays undefined (so it renders the check). The class relied on that tri-state, and giving this a
   * default either way collapses it — `default: false` loses the explicit case, `default: void 0` puts a
   * check icon on every button in the apps.
   */
  active: { type: Boolean },
  disable: { type: Boolean, default: false },
  large: { type: Boolean, default: false },
  asLink: { type: Boolean, default: false },
  flat: { type: Boolean, default: false },
  iconOnly: { type: Boolean, default: false }
}

export interface BsBtnProps extends BsComponentProps {
  secondary?: boolean
  warning?: boolean
  active?: boolean | undefined
  disable?: boolean
  large?: boolean
  asLink?: boolean
  flat?: boolean
  iconOnly?: boolean
}

export function useBsBtn (props: BsBtnProps, emit: (event: string, ...args: unknown[]) => void) {
  const vm = useVm()
  const bs = useBsComponent({ vm, props, emit })

  const clickCounter = ref(0)

  /**
   * NOTE, carried over: nothing reads this — no template binds it, so a submit button in an invalid
   * form is still not actually disabled. It is at least correct now: `isFormValid` used to return the
   * inverse of the tree's validity, which made this read "disabled while the form is VALID".
   */
  const isDisabled = computed(() =>
    // `?.` on `props` only. A mounted component always has its instance and its vnode, but a template
    // that gives `<bs-btn>` no attributes at all renders it with `props: null`.
    vm.$.vnode.props?.type === 'submit' && !bs.isFormValid.value)

  const getBtnProps = () => bs.getRenderProps({
    disable: props.disable,
    outline: false
  })

  const getBtnClasses = () => bs.getClasses({
    'bs-btn--active': props.active,
    'bs-btn--secondary': props.secondary,
    'bs-btn--disabled': props.disable,
    'bs-btn--warning': props.warning,
    'bs-btn--large': props.large,
    'bs-btn--as-link': props.asLink,
    'bs-btn--flat': props.flat,
    'bs-btn--icon-only': props.iconOnly
  })

  /**
   * Swallows everything but the first click in a 500ms window. A double-tapped submit was sending two
   * of whatever it submitted.
   */
  const clickHandler = () => ({
    click: (v: any) => {
      clickCounter.value += 1

      if (clickCounter.value === 1) {
        emit('click', v)
      }

      setTimeout(() => {
        clickCounter.value = 0
      }, 500)
    }
  })

  return {
    ...bs,
    clickCounter,
    isDisabled,
    getBtnProps,
    getBtnClasses,
    clickHandler
  }
}

export default defineComponent({
  name: 'BsBtn',

  /**
   * Vue 3 merges attribute fallthrough OVER what the render sets, so a caller's `color` beat the error
   * colour this component computes -- an invalid field kept the caller's colour instead of turning red.
   * The render forwards `$attrs` itself, first, so its own values win.
   */
  inheritAttrs: false,

  // See BsInput: `click` is a DOM event name, so leaving it undeclared binds a caller's `@click` as a
  // native root listener as well as a component one, and the handler runs twice.
  emits: [ 'click' ],

  props: bsBtnProps,

  setup (props, { emit }) {
    return { ...useBsBtn(props, emit) }
  },

  methods: {
    /** Renamed from `__renderActiveIcon`: Vue does not proxy keys beginning with `_`. */
    renderActiveIcon () {
      if (this.active === false) return void 0

      return h(QIcon, {
        class: 'bs-btn-active__icon',
        name: 'icon-check'
      })
    }
  },

  render () {
    return h(QBtn, {
      ...this.$attrs,
      // The caller's class is merged, not replaced: with `inheritAttrs: false` Vue no longer does it.
      class: [this.$attrs.class, 'bs-btn', this.getBtnClasses()],
      ...this.getBtnProps(),
      ...quasarHandlers(this.getListeners(this.clickHandler())),
      // `$attrs` is spread at the top of this bag, not here: repeating it after getBtnProps put the
      // caller's values back over the ones this component computes.
      ...this.getAttributes({
        ripple: !this.iconOnly && !this.asLink
      })
    // QBtn is a component: its children have to be a slot function, not an array.
    }, {
      default: () => [
        this.renderActiveIcon(),
        this.slot(this, 'default')
      ]
    })
  }
})
